import React, { useState, useEffect, useCallback, Component } from 'react';

// Error boundary to catch silent React crashes
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  componentDidCatch(e, info) { console.error('ErrorBoundary caught:', e, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, textAlign: 'center', color: '#dc2626' }}>
          <p style={{ fontWeight: 700, fontSize: 16 }}>Something went wrong loading this page.</p>
          <p style={{ fontSize: 13, color: '#6b7280' }}>{this.state.error.message}</p>
          <button onClick={() => this.setState({ error: null })}
            style={{ marginTop: 12, padding: '8px 20px', borderRadius: 8, border: '1px solid #d1d5db', cursor: 'pointer' }}>
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
import { api } from './api';
import Login from './components/Login';
import Header from './components/Header';
import AdminShell from './components/AdminShell';
import JobFormV2 from './components/JobFormV2';
import ContractorDashboard from './components/ContractorDashboard';
import OfficeCleaner from './components/OfficeCleaner';
import AdminDashboard from './components/AdminDashboard';
import AdminDashboardHome from './components/AdminDashboardHome';
import AttendancePage from './components/AttendancePage';
import AdminReportsPage from './components/AdminReportsPage';
import AdminSettingsPage from './components/AdminSettingsPage';
import FinancePage from './components/FinancePage';
import PrivatePage from './components/PrivatePage';
import ContractorManagement from './components/ContractorManagement';
import MyAccount from './components/MyAccount';
import InvoiceView from './components/InvoiceView';
import StatementMobile from './components/StatementMobile';
import MyJobsPanel from './components/MyJobsPanel';
import BuffingFormV2 from './components/BuffingFormV2';
import BuffingAdmin from './components/BuffingAdmin';
import SignupPage from './components/SignupPage';
import ContractorAgreementPage from './components/ContractorAgreementPage';
import InsuranceCenter from './components/InsuranceCenter';
import AgreementsPanel from './components/AgreementsPanel';
import { CustomerProvider } from './contexts/CustomerContext';
import WorkspaceSwitcher from './components/WorkspaceSwitcher';
import MelvilleQueue from './components/MelvilleQueue';
import PrivateBookings from './components/PrivateBookings';
import HourlyMeter from './components/HourlyMeter';

// The queue a detailer opens has two sources now: Melville's cars, which come
// over the bridge, and private cars booked off an accepted quote. Booked
// private cars go on top — there are rarely more than a couple, and they have
// a day attached.
function DetailQueueScreen({ onPick, isAdmin }) {
  return (
    <>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '14px 16px 0' }}>
        <PrivateBookings onPick={onPick} />
      </div>
      <MelvilleQueue onPickCar={onPick} isAdmin={isAdmin} />
    </>
  );
}

function parseJwt(token) {
  try { return JSON.parse(atob(token.split('.')[1])); } catch { return null; }
}

// Detect signup URL (e.g. /signup/abc123)
const signupMatch = window.location.pathname.match(/^\/signup\/(.+)$/);

// Detect contractor agreement public page
const isAgreementPage = window.location.pathname === '/contractor-agreement';

export default function App() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState(null);
  const [showAccount, setShowAccount] = useState(false);
  // Melville integration: only show the tab when the bridge is configured on
  // the server (local dev). Dormant in production until Melville is deployed.
  const [melvilleConfigured, setMelvilleConfigured] = useState(false);
  // When a detailer taps a car in the Melville queue, we stash its details
  // here and switch to the Log Job / Submit Job tab so JobForm pre-fills.
  const [melvillePrefill, setMelvillePrefill] = useState(null);
  // Bumped after an hourly worker submits a job so the live production meter
  // refreshes immediately instead of waiting for its 45s poll.
  const [hourlyReload, setHourlyReload] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('acts_token');
    if (token) {
      const payload = parseJwt(token);
      if (payload && payload.exp * 1000 > Date.now()) {
        setUser({ id: payload.id, username: payload.username, name: payload.name, type: payload.type });
      } else {
        localStorage.removeItem('acts_token');
      }
    }
  }, []);

  // Probe the Melville bridge once logged in. Detailers (commissioned/hourly)
  // need the queue too, not just admin — so probe for any user.
  useEffect(() => {
    if (!user) { setMelvilleConfigured(false); return; }
    api.melvilleStatus()
      .then(r => setMelvilleConfigured(!!r.configured))
      .catch(() => setMelvilleConfigured(false));
  }, [user]);

  // ── Today's earnings for commissioned contractors ─────────────────────────
  // NOTE: ALL hooks must be declared before any conditional returns (Rules of Hooks)
  const [todayEarnings, setTodayEarnings] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  const loadTodayEarnings = useCallback(() => {
    if (!user || user.type !== 'commissioned') return;
    api.getJobs({ date: today })
      .then(jobs => {
        const total = jobs.reduce((sum, j) => sum + (parseFloat(j.my_payout) || 0), 0);
        const pending = jobs.some(j => j.status === 'pending');
        setTodayEarnings({ total, pending, count: jobs.length });
      })
      .catch(() => {});
  }, [user, today]);

  useEffect(() => { loadTodayEarnings(); }, [loadTodayEarnings]);

  // Signup page — no auth needed
  if (!user && signupMatch) {
    return <SignupPage token={signupMatch[1]} />;
  }

  // Contractor agreement public page — no auth needed
  if (isAgreementPage) {
    return <ContractorAgreementPage />;
  }

  function handleLogin(userData) {
    setUser(userData);
    if (userData.type === 'admin') setTab('Dashboard');
    else if (userData.type === 'office_cleaner') setTab('My Day');
    else setTab('Submit Job');
  }

  // Melville queue → Log Job pre-fill. Admin logs via the "Log Job" tab,
  // contractors via "Submit Job". Stash the car, then jump there.
  // Opens the Log Job form for a Melville car. By the time this runs the car
  // is already In Progress (the queue's first tap started it + the clock), and
  // the Submit tap stops the clock — so this only pre-fills + navigates.
  function handleMelvilleDetail(car) {
    setMelvillePrefill(car);
    setTab(user?.type === 'admin' ? 'Log Job' : 'Submit Job');
  }

  function handleLogout() {
    localStorage.removeItem('acts_token');
    setUser(null);
    setTab(null);
  }

  if (!user) return <Login onLogin={handleLogin} />;

  // Tab lists by user type
  const tabsByType = {
    admin:          ['Dashboard', 'Jobs', 'Log Job', 'Attendance', 'Reports', 'Private', 'Finance',
                     ...(melvilleConfigured ? ['Detail Queue'] : []), 'Settings'],
    // Detail Queue leads for detailers — it's the landing screen (tabs[0]).
    commissioned:   [...(melvilleConfigured ? ['Detail Queue'] : []), 'Submit Job', 'My Jobs', 'Buffing', 'Insurance', 'Invoice', 'Statement'],
    hourly:         [...(melvilleConfigured ? ['Detail Queue'] : []), 'Submit Job', 'My Jobs', 'Buffing', 'Insurance', 'Invoice', 'Statement'],
    office_cleaner: ['My Day', 'My Records', 'Insurance', 'Invoice', 'Statement'],
  };
  const tabs = tabsByType[user.type] || ['My Jobs'];
  const currentTab = tab || tabs[0];

  // Detect invoice tabs — used to hide navbar for clean invoice view
  const isInvoiceTab = currentTab === 'Invoice';

  // Default "back" tab per user type
  const backTab = user.type === 'admin' ? 'Jobs'
                : user.type === 'office_cleaner' ? 'My Day'
                : 'My Jobs';

  function renderContent() {
    if (user.type === 'admin') {
      if (currentTab === 'Dashboard')  return <AdminDashboardHome user={user} />;
      if (currentTab === 'Jobs')       return <AdminDashboard user={user} view="jobs" />;
      // "Log Job" lets admin create a job on behalf of any detailer who
      // forgot to submit their own. Reuses the regular JobForm — the form's
      // existing isAdmin branch already surfaces the full employee picker.
      if (currentTab === 'Log Job')    return <JobFormV2 user={user} onJobSubmitted={() => { setMelvillePrefill(null); setTab('Jobs'); }} onViewMyJobs={() => setTab('Jobs')} prefill={melvillePrefill} />;
      if (currentTab === 'Attendance') return <AttendancePage />;
      if (currentTab === 'Reports')    return <AdminReportsPage user={user} onBack={() => setTab('Jobs')} />;
      if (currentTab === 'Private')    return <PrivatePage />;
      if (currentTab === 'Finance')    return <FinancePage />;
      if (currentTab === 'Detail Queue')   return <DetailQueueScreen onPick={handleMelvilleDetail} isAdmin={user.type === 'admin'} />;
      if (currentTab === 'Settings')   return <AdminSettingsPage />;
    }

    if (user.type === 'office_cleaner') {
      if (currentTab === 'My Day') return <OfficeCleaner user={user} />;
      if (currentTab === 'My Records') return <ContractorDashboard user={user} />;
      if (currentTab === 'Insurance')  return <InsuranceCenter user={user} />;
      if (currentTab === 'Invoice') return <InvoiceView user={user} onBack={() => setTab(backTab)} />;
      if (currentTab === 'Statement') return <StatementMobile />;
    }

    // commissioned + hourly
    if (currentTab === 'Submit Job') return <JobFormV2 user={user} onJobSubmitted={() => { setMelvillePrefill(null); loadTodayEarnings(); setHourlyReload(n => n + 1); }} onViewMyJobs={() => setTab('My Jobs')} prefill={melvillePrefill} />;
    if (currentTab === 'My Jobs')    return <MyJobsPanel user={user} />;
    if (currentTab === 'My Records') return <ContractorDashboard user={user} />;
    if (currentTab === 'Buffing')    return <BuffingFormV2 user={user} />;
    if (currentTab === 'Insurance')  return <InsuranceCenter user={user} />;
    if (currentTab === 'Detail Queue')   return <DetailQueueScreen onPick={handleMelvilleDetail} isAdmin={user.type === 'admin'} />;
    if (currentTab === 'Invoice')    return <InvoiceView user={user} onBack={() => setTab(backTab)} />;
    if (currentTab === 'Statement')  return <StatementMobile />;

    return null;
  }

  // ── Admin gets the new sidebar shell ──
  if (user.type === 'admin' && !isInvoiceTab) {
    return (
      <CustomerProvider user={user}>
        <AdminShell
          user={user}
          currentTab={currentTab}
          onTabChange={setTab}
          onAccount={() => setShowAccount(true)}
          onLogout={handleLogout}
          headerSlot={<WorkspaceSwitcher onManage={() => setTab('Settings')} />}
          showMelville={melvilleConfigured}
        >
          <ErrorBoundary key={currentTab}>
            {renderContent()}
          </ErrorBoundary>
        </AdminShell>
        {showAccount && (
          <MyAccount user={user} onClose={() => setShowAccount(false)} />
        )}
      </CustomerProvider>
    );
  }

  return (
    <CustomerProvider user={user}>
    <div style={{ minHeight: '100vh' }}>
      <Header
        user={user}
        onLogout={handleLogout}
        currentTab={currentTab}
        onTabChange={setTab}
        onAccount={() => setShowAccount(true)}
        showMelville={melvilleConfigured}
      />
      {/* Live daily production meter — hourly workers only (self-guards).
          On mobile it's hidden for hourly: the redesigned dial header already
          shows production. Kept on desktop (class toggled via CSS). */}
      {!isInvoiceTab && (
        <div className="hourlymeter-standalone">
          <HourlyMeter user={user} reloadSignal={hourlyReload} date={today} />
        </div>
      )}
      {/* Profile-incomplete alert now lives in the header bell (NotificationBell),
          not a yellow banner. */}

      {/* Today's earnings banner — commissioned only (not on the invoice).
          Hidden on mobile: the redesigned earnings-dial header already shows
          today's earnings there (class toggled via CSS). */}
      {user.type === 'commissioned' && todayEarnings !== null && !isInvoiceTab && (
        <div className="no-print earnings-banner-standalone" style={{
          background: todayEarnings.pending ? '#fef3c7' : '#dcfce7',
          borderBottom: `2px solid ${todayEarnings.pending ? '#f59e0b' : '#16a34a'}`,
          padding: '8px 20px',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#1f2937' }}>
            💰 Today's earnings: <span style={{ color: todayEarnings.pending ? '#92400e' : '#15803d', fontSize: 18 }}>
              ${todayEarnings.total.toFixed(2)}
            </span>
            {todayEarnings.count > 0 && (
              <span style={{ fontWeight: 400, fontSize: 13, color: '#6b7280', marginLeft: 6 }}>
                ({todayEarnings.count} job{todayEarnings.count !== 1 ? 's' : ''})
              </span>
            )}
          </span>
          {todayEarnings.pending && (
            <span style={{ fontSize: 12, color: '#92400e', background: '#fde68a', borderRadius: 6, padding: '2px 8px', fontWeight: 600 }}>
              ⏳ Pending admin approval
            </span>
          )}
          {todayEarnings.count === 0 && (
            <span style={{ fontSize: 13, color: '#6b7280' }}>No jobs submitted today yet.</span>
          )}
        </div>
      )}
      <main className={user.type !== 'admin' ? 'contractor-main' : ''} style={{ paddingBottom: 40 }}>
        <ErrorBoundary key={currentTab}>
          {renderContent()}
        </ErrorBoundary>
      </main>

      {showAccount && (
        <MyAccount user={user} onClose={() => setShowAccount(false)} />
      )}
    </div>
    </CustomerProvider>
  );
}
